import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Repeat, Plus, Trash2, Play, Pause, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const RecurringTasksPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/recurring-tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
      setIsLoading(false);
    } catch (error) {
      toast.error('Failed to load recurring tasks');
      setIsLoading(false);
    }
  };

  const handleGenerateTask = async (templateId, templateName) => {
    if (!confirm(`Generate a new task from "${templateName}"?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/recurring-tasks/${templateId}/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task generated successfully!');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to generate task');
    }
  };

  const handleToggleActive = async (templateId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/recurring-tasks/${templateId}`, {
        is_active: !currentStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Template ${!currentStatus ? 'activated' : 'paused'}`);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleDelete = async (templateId, templateName) => {
    if (!confirm(`Delete recurring task template "${templateName}"?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/recurring-tasks/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted successfully!');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const getFrequencyLabel = (template) => {
    const freq = template.frequency.replace('_', ' ');
    const value = template.frequency_value > 1 ? `Every ${template.frequency_value} ` : '';
    return value + freq.charAt(0).toUpperCase() + freq.slice(1);
  };

  return (
    <AdminLayout>
      <Toaster />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/tasks')}
              className="text-gray-400 hover:text-white hover:bg-gray-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <Repeat className="w-8 h-8 text-orange-500" />
                Recurring Tasks
              </h1>
              <p className="text-gray-400 mt-2">Manage recurring task templates</p>
            </div>
          </div>
          <Button onClick={() => toast.info('Create recurring task - Coming soon')} className="bg-orange-600 hover:bg-orange-700">
            <Plus className="w-4 h-4 mr-2" />
            Create Template
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <Card key={template.id} className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-white">{template.template_name}</CardTitle>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className="bg-orange-600 text-white">{getFrequencyLabel(template)}</Badge>
                      <Badge className={template.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                        {template.is_active ? 'Active' : 'Paused'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm space-y-1">
                  <p className="text-gray-400">{template.client_name} - {template.site_name}</p>
                  <p className="text-gray-500">{template.service_routine}</p>
                  {template.assets && template.assets.length > 0 && (
                    <p className="text-gray-500">{template.assets.length} asset(s)</p>
                  )}
                </div>

                <div className="text-xs space-y-1">
                  {template.last_generated_date && (
                    <p className="text-gray-500">
                      Last: {new Date(template.last_generated_date).toLocaleDateString()}
                    </p>
                  )}
                  {template.next_generation_date && (
                    <p className="text-gray-500">
                      Next: {new Date(template.next_generation_date).toLocaleDateString()}
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleGenerateTask(template.id, template.template_name)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Generate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleToggleActive(template.id, template.is_active)}
                    className="bg-gray-700 border-gray-600"
                  >
                    {template.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(template.id, template.template_name)}
                    className="bg-red-900/20 border-red-900/30 text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {templates.length === 0 && !isLoading && (
          <Card className="bg-gray-800 border-gray-700 text-center p-12">
            <Repeat className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No recurring tasks found</h3>
            <p className="text-gray-400 mb-6">Create templates to auto-generate routine tasks</p>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default RecurringTasksPage;
